import { TDictionary, TDropDownItem, TDropDownList } from '@/types';
import { cloneDeep } from 'lodash';

export type TSerializeDataByParams<T> = { [byParams: string]: T };

export const serializeDataByParams = (
  data: Array<any>,
  byParams: string
): TSerializeDataByParams<any> => {
  const serializeData: TSerializeDataByParams<any> = {};

  try {
    for (const item of data) {
      serializeData[item[byParams]] = item;
    }
  } catch (err) {
    console.error('serializeDataByParams: ', err);
  }

  return serializeData;
};

export const changeArrayValues = (
  values: Array<string | number>,
  value: string | number
): Array<string | number> => {
  let newValues: Array<string | number> = [];

  try {
    if (values.includes(value)) {
      newValues = values.filter((sc) => sc !== value);
    } else {
      newValues = [...values, value];
    }
  } catch (err) {
    console.error('changeArrayValues: ', err);
  }

  return newValues;
};

export const changeDropDownListValues = (
  dropDownList: TDropDownList,
  item: TDropDownItem
): TDropDownList => {
  let newList = [] as TDropDownList;

  try {
    const values = dropDownList.map((dl) => dl.value);

    if (values.includes(item.value)) {
      newList = dropDownList.filter((dl) => dl.value !== item.value);
    } else {
      newList = [...dropDownList, item];
    }
  } catch (err) {
    console.error('changeDropDownListValues: ', err);
  }

  return newList;
};

export const filterArrayByParam = (
  data: Array<any>,
  param: string
): Array<any> => {
  const newData = [] as Array<any>;
  let itemData;

  try {
    for (const item of data) {
      if (item[param]) {
        itemData = cloneDeep(item);
        newData.push(itemData);
      }
    }
  } catch (err) {
    console.error('filterArrayByParam: ', err);
  }

  return newData;
};

export const getObjectFromArray = (
  data: Array<any>,
  param: string,
  value: string | number
): any | null => {
  try {
    const index = data.findIndex(
      (item) => String(item[param]) === String(value)
    );

    if (index !== -1) {
      return cloneDeep(data[index]);
    }
  } catch (err) {
    console.error('getObjectFromArray: ', err);
  }

  return null;
};

export const extrudeArrayFromArray = (
  mainData: Array<any>,
  extrudedData: Array<any>,
  param: string
): Array<any> => {
  const newMainData = [] as Array<any>;
  const extrudedParams = {};

  try {
    for (const item of extrudedData) {
      extrudedParams[item[param]] = true;
    }

    for (const item of mainData) {
      if (!extrudedParams[item[param]]) {
        newMainData.push(item);
      }
    }
  } catch (err) {
    console.error('extrudeArrayFromArray: ', err);
  }

  return newMainData;
};

export const getRandomIntNumber = (maxValue: number): number => {
  return Math.floor(Math.random() * maxValue);
};

export const getRandomFloatNumber = (maxValue: number): number => {
  return Number(Math.random() * maxValue);
};

export const formatDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${day < 10 ? '0' + day : day}.${
    month < 10 ? '0' + month : month
  }.${year}`;
};

export const numberIsInt = (value: number): boolean => {
  return Number(value) === value && value % 1 === 0;
};

export const numberIsFloat = (value: number): boolean => {
  return Number(value) === value && value % 1 !== 0;
};

export const formatNumberByOffset = (value: number, locale = 'ru'): string => {
  const options = {
    minimumFractionDigits: 0,
  };

  return value.toLocaleString(locale, options);
};

export const formatNumberByFixed = (value: number, digits = 2): string => {
  if (numberIsFloat(value)) {
    return value.toFixed(digits);
  }
  return String(value);
};

export const isNumber = (value: string | number): boolean => {
  return typeof value === 'number';
};

export const isString = (value: string | number): boolean => {
  return typeof value === 'string';
};

export const isArray = (value: any): boolean => {
  return Array.isArray(value);
};

export const isObject = (value: any): boolean => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return true;
  }
  return false;
};

export const sortObjectByKey = (obj: TDictionary<any>): TDictionary<any> => {
  return Object.fromEntries(Object.entries(obj).sort());
};

export const sortArrayToDropDownListByParams = (
  array: Array<any>,
  label: string,
  value: string | number
): TDropDownList => {
  const dropDownList = [] as TDropDownList;

  try {
    for (const item of array) {
      dropDownList.push({
        label: item[label],
        value: item[value],
      });
    }
  } catch (err) {
    console.error('sortObjectToDropDownList: ', err);
  }

  return dropDownList;
};
